import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import translations from './translations';
import './ResultSubmission.css';
import { getTimeTakenSafe } from '../utils/timing';
import { API_BASE_URL, API_TOKEN, STORAGE_KEYS } from '../config';
import { safeGet, safeSet, safeRemove } from '../utils/storage';
import { getCommonParams } from '../utils/url';
import { getAssessmentConfig } from '../data/registry';
import { createSubmissionId, resolveSubmissionContext } from '../utils/submissionRecovery';
import { fetchCheckpointAnswers, queueAnswerSnapshot } from '../utils/checkpoint';
import { assessmentMetadata, isAdultAssessment, uiLanguage, addMetadataToParams } from '../utils/assessmentLanguage';
import { loadAdultQuestionBank, bankAnswerCacheKey, bankOrderCacheKey } from '../utils/adultQuestionBank';

function ResultSubmission() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [evaluationUuid, setEvaluationUuid] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [resumeAvailable, setResumeAvailable] = useState(false);
  const submitRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const scheduleRetry = () => {
    clearRetryTimer();
    const delay = Math.min(10000 * (2 ** retryAttemptRef.current), 60000);
    retryAttemptRef.current += 1;
    retryTimerRef.current = window.setTimeout(() => {
      void submitRef.current?.({ automatic: true });
    }, delay);
  };

  useEffect(() => {
    const retryPendingSubmission = () => {
      if (safeGet(STORAGE_KEYS.PENDING_SUBMISSION, null)) {
        void submitRef.current?.({ automatic: true });
      }
    };
    window.addEventListener('online', retryPendingSubmission);
    if (safeGet(STORAGE_KEYS.PENDING_SUBMISSION, null)) {
      retryTimerRef.current = window.setTimeout(retryPendingSubmission, 1000);
    }
    return () => {
      window.removeEventListener('online', retryPendingSubmission);
      clearRetryTimer();
    };
  }, []);

  const userInfo = safeGet(STORAGE_KEYS.USER_INFO, {});
  const activeSession = safeGet(STORAGE_KEYS.ACTIVE_SESSION, {});
  const storedSessions = safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {});
  const submissionContext = resolveSubmissionContext({
    userInfo,
    urlParams: getCommonParams(),
    activeSession,
    sessions: storedSessions,
  });
  const assessmentType = safeGet(STORAGE_KEYS.ASSESSMENT_TYPE, 'adult14_hr');
  const assessmentConfig = getAssessmentConfig(assessmentType);

  const lang = uiLanguage(submissionContext.assessment_language || submissionContext.defaultLang);

  const tRoot = translations[lang] || translations['zh-TW'];
  const t = tRoot.resultSubmission;
  const errorMap = tRoot.errorMessages || {};
  const feedback = tRoot.submissionFeedback;

  const submitResults = async ({ automatic = false } = {}) => {
    if (submitted || loading) return;

    setLoading(true);
    setMessage('');
    setEvaluationUuid('');
    setCopyStatus('');
    setResumeAvailable(false);

    // 使用 config.js 中的配置
    const accessToken = API_TOKEN;
    let quizAnswers = safeGet(STORAGE_KEYS.QUIZ_ANSWERS, {});
    const pendingSubmission = safeGet(STORAGE_KEYS.PENDING_SUBMISSION, null);
    const timeTaken = getTimeTakenSafe();

    let currentContext = resolveSubmissionContext({
      userInfo: safeGet(STORAGE_KEYS.USER_INFO, {}),
      urlParams: getCommonParams(),
      activeSession: safeGet(STORAGE_KEYS.ACTIVE_SESSION, {}),
      sessions: safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {}),
    });
    if (isAdultAssessment(assessmentType) && !currentContext.question_bank_version) {
      try {
        await fetchCheckpointAnswers(currentContext);
        currentContext = resolveSubmissionContext({ userInfo: safeGet(STORAGE_KEYS.USER_INFO, {}),
          urlParams: getCommonParams(), activeSession: safeGet(STORAGE_KEYS.ACTIVE_SESSION, {}),
          sessions: safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {}) });
        await loadAdultQuestionBank(currentContext);
        currentContext = resolveSubmissionContext({ userInfo: safeGet(STORAGE_KEYS.USER_INFO, {}),
          urlParams: getCommonParams(), activeSession: safeGet(STORAGE_KEYS.ACTIVE_SESSION, {}),
          sessions: safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {}) });
        quizAnswers = safeGet(STORAGE_KEYS.QUIZ_ANSWERS, {});
      } catch (error) {
        setMessage(tRoot.questionLoadFailed);
        setLoading(false);
        return;
      }
    }
    const {
      defaultLang,
      useruuid,
      evaluation_uuid,
      name,
      email,
      phone,
      birthYear,
      gender,
      companyCode,
      Job_NID: jobNID,
      User_NID: userNID,
      codeType,
      evaluation_type: evaluationType,
    } = currentContext;

    const e_data = {
      ...(pendingSubmission?.e_data || {}),
      time: timeTaken,
      defaultLang,
      ...assessmentMetadata({ ...currentContext, ...(pendingSubmission?.e_data || {}) }),
      submission_id: pendingSubmission?.e_data?.submission_id || createSubmissionId(),
    };
    e_data.defaultLang = uiLanguage(e_data.assessment_language);
    if (useruuid) e_data.useruuid = useruuid;
    if (evaluation_uuid) e_data.evaluation_uuid = evaluation_uuid;
    
    // 只有当URL中存在这些参数时才添加到 e_data 中
    if (companyCode) {
      e_data.companyCode = companyCode;
    }
    if (jobNID) {
      e_data.Job_NID = jobNID;
    }
    if (userNID) {
      e_data.User_NID = userNID;
    }
    if (codeType) {
      e_data.codeType = codeType;
    }
    if (evaluationType) {
      e_data.evaluation_type = evaluationType;
    }
    
    // 如果用户信息中包含基本信息（通过表单填写的），则添加到e_data中
    if (name) {
      e_data.username = name;
    }
    if (email) {
      e_data.useremail = email;
    }
    if (phone) {
      e_data.userphone = phone;
    }
    if (birthYear !== undefined) {
      e_data.year = birthYear;
    }
    if (gender !== undefined) {
      e_data.usersex = gender;
    }
    if (currentContext.ageGroup) {
      e_data.ageGroup = currentContext.ageGroup;
    }

    let e_content = Object.entries(quizAnswers).map(([no, fraction]) => ({
      no: parseInt(no, 10),
      fraction: parseInt(fraction, 10),
    }));
    if (!e_content.length && Array.isArray(pendingSubmission?.e_content)) {
      e_content = pendingSubmission.e_content;
    }

    const expectedCount = typeof assessmentConfig.getTotalQuestions === 'function'
      ? assessmentConfig.getTotalQuestions(currentContext.ageGroup)
      : (assessmentConfig.totalQuestions || 0);
    if (!expectedCount || e_content.length < expectedCount) {
      try {
        const checkpointAnswers = await fetchCheckpointAnswers(currentContext);
        const mergedAnswers = new Map();
        [...checkpointAnswers, ...e_content].forEach((item) => {
          const no = parseInt(item?.no, 10);
          const fraction = parseInt(item?.fraction, 10);
          if (no > 0 && [0, 1, 2].includes(fraction)) mergedAnswers.set(no, fraction);
        });
        e_content = Array.from(mergedAnswers.entries()).map(([no, fraction]) => ({ no, fraction }));
        if (e_content.length) {
          safeSet(STORAGE_KEYS.QUIZ_ANSWERS, Object.fromEntries(
            e_content.map((item) => [item.no, String(item.fraction)])
          ));
        }
      } catch (error) {
        console.warn('[checkpoint] Unable to restore server answers:', error?.message || error);
      }
    }

    if (!e_content.length) {
      setMessage(feedback.noAnswers);
      setLoading(false);
      return;
    }
    if (expectedCount && e_content.length < expectedCount) {
      setMessage(feedback.restored(e_content.length, expectedCount - e_content.length));
      setResumeAvailable(true);
      setLoading(false);
      return;
    }

    const createdAt = Math.floor(Date.now() / 1000);

    const postData = {
      outrep: assessmentConfig.outrep || 'career',
      e_data,
      e_content,
      created_at: createdAt,
    };
    safeSet(STORAGE_KEYS.PENDING_SUBMISSION, postData);
    const finalAnswerSnapshot = Object.fromEntries(
      e_content.map((item) => [item.no, item.fraction])
    );
    const checkpointSaved = await queueAnswerSnapshot(finalAnswerSnapshot);

    // 使用 config.js 中的 API_BASE_URL
    const apiurl = `${API_BASE_URL}/api/assessment/evaluation`;

    try {
      const response = await axios.post(apiurl, postData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        clearRetryTimer();
        retryAttemptRef.current = 0;
        setMessage(`✅ ${t.success}`);
        setSubmitted(true);
        setEvaluationUuid(response.data.evaluation_uuid || '');
        safeRemove(STORAGE_KEYS.PENDING_SUBMISSION);
        safeRemove(STORAGE_KEYS.UNSYNCED_ANSWERS);
        
        // 清理测评相关的localStorage数据
        safeRemove(STORAGE_KEYS.START_TIME);
        safeRemove(STORAGE_KEYS.TIME_TAKEN);
        safeRemove(STORAGE_KEYS.QUIZ_ANSWERS);
        const ageGroupSuffix = currentContext.ageGroup ? `_${currentContext.ageGroup}` : '';
        const orderKey = `${STORAGE_KEYS.QUIZ_ORDER_PREFIX}${assessmentType}${ageGroupSuffix}_${defaultLang || lang}`;
        safeRemove(orderKey);
        if (isAdultAssessment(assessmentType)) {
          safeRemove(bankOrderCacheKey(currentContext));
          safeRemove(bankAnswerCacheKey(currentContext));
        }
        
        // 将用户标记为已完成测评
        const completedEvaluations = safeGet(STORAGE_KEYS.COMPLETED_EVALUATIONS, []);
        if (!completedEvaluations.includes(useruuid)) {
          completedEvaluations.push(useruuid);
          safeSet(STORAGE_KEYS.COMPLETED_EVALUATIONS, completedEvaluations);
        }
        
        // 清除该用户的未完成会话记录
        const sessions = safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {});
        if (sessions[useruuid]) {
          delete sessions[useruuid];
          safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions);
        }
        const currentActiveSession = safeGet(STORAGE_KEYS.ACTIVE_SESSION, {});
        if (!evaluation_uuid || currentActiveSession.evaluation_uuid === evaluation_uuid) {
          safeRemove(STORAGE_KEYS.ACTIVE_SESSION);
        }
      } else if (response.status === 202) {
        setMessage(feedback.delivered);
        setEvaluationUuid(
          response.data.evaluation_uuid || response.data.recovery_uuid || ''
        );
        safeRemove(STORAGE_KEYS.UNSYNCED_ANSWERS);
        scheduleRetry();
      } else {
        setMessage(feedback.processing);
        scheduleRetry();
      }
    } catch (error) {
      const errorCode = error.response?.data?.error_code;
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message;

      const translatedError =
        errorCode && errorMap[errorCode]
          ? errorMap[errorCode]
          : backendMessage;

      const detail = automatic ? '' : `（${translatedError}）`;
      if (checkpointSaved) {
        setMessage(`${feedback.serverSaved}${detail}`);
      } else {
        setMessage(`${feedback.deviceSaved}${detail}`);
      }
      scheduleRetry();
    } finally {
      setLoading(false);
    }
  };

  submitRef.current = submitResults;

  const handleCopy = () => {
    if (evaluationUuid) {
      navigator.clipboard.writeText(evaluationUuid).then(() => {
        setCopyStatus(t.copied);
        setTimeout(() => setCopyStatus(''), 2000);
      });
    }
  };

  return (
    <div className="result-submission-container">
      <div className="result-submission-card">
        <h1>{t.title}</h1>
        <button
          onClick={submitResults}
          disabled={submitted || loading}
          className="submit-button"
        >
          {loading ? t.buttonLoading : t.button}
        </button>

        {message && <div className="submission-message">{message}</div>}

        {resumeAvailable && (
          <button
            type="button"
            className="submit-button"
            onClick={() => navigate(`/quiz?${addMetadataToParams(new URLSearchParams({ defaultLang: submissionContext.defaultLang || lang, resume: '1' }), submissionContext).toString()}`)}
          >
            {tRoot.continueRemaining}
          </button>
        )}

        {evaluationUuid && (
          <div className="submission-message">
            {t.evaluationCode}：<strong>{evaluationUuid}</strong>{' '}
            <button onClick={handleCopy} style={{ marginLeft: '10px' }}>
              {t.copy}
            </button>
            {copyStatus && (
              <span style={{ marginLeft: '8px', color: 'green' }}>
                {copyStatus}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ResultSubmission;
